import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { setGlobalTokenAndOrganizationId } from "../../axiosConfig";
import { AxiosError } from "axios";
import {
  ClassroomResponseDto,
  TeacherResponseDto,
} from "../api/madlenAPI.schemas";
import { RootState } from "./store";
import {
  editPreferences,
  firebaseLogin,
  getLoggedInTeacher,
} from "../api/madlenAPI";
import i18next from "i18next";
import i18n from "@packages/i18n/config";
import posthog from "posthog-js";
import * as Sentry from "@sentry/react";
import ReactGA from "react-ga4";
import { PdfCustomizationSettings } from "../../features/common/components/pdf-customization-modal";

interface UserState {
  token: string | null;
  teacher: TeacherResponseDto | null;
  organizationId: number | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  avatar: string | null;
}
const defaultPdfOptions: PdfCustomizationSettings = {
  showSchoolLogo: true,
  schoolLogoPosition: "topRight",
  madlenLogoPosition: "center",
  showDate: false,
  showTeacherName: false,
  fontSize: "medium",
  showGeneratedImage: true,
};
// Load initial state from localStorage if available
const loadState = (): UserState => {
  try {
    const serializedState = localStorage.getItem("userState");
    if (serializedState === null) {
      return {
        token: null,
        teacher: null,
        organizationId: null,
        isAuthenticated: false,
        loading: false,
        error: null,
        avatar: null,
      };
    }
    setGlobalTokenAndOrganizationId(
      JSON.parse(serializedState).token,
      JSON.parse(serializedState).organizationId,
    );
    return JSON.parse(serializedState);
  } catch (err) {
    return {
      token: null,
      teacher: null,
      organizationId: null,
      isAuthenticated: false,
      loading: false,
      error: null,
      avatar: null,
    };
  }
};

const initialState: UserState = loadState();

// Async thunk for login
export const loginUser = createAsyncThunk(
  "user/login",
  async (
    userData: {
      token: string;
      organizationId: number;
      userType: string;
      name: string;
      surname: string;
      language: string;
      avatar: string;
    },
    { rejectWithValue },
  ) => {
    const response = await firebaseLogin(userData);
    if (response.customToken) {
      const firebaseRes = await signInWithCustomToken(
        getAuth(),
        response.customToken,
      );
      const token = await firebaseRes.user.getIdToken();
      setGlobalTokenAndOrganizationId(token, userData.organizationId);
      try {
        const teacher = await getLoggedInTeacher();
        if (teacher?.preferences?.language) {
          i18n.changeLanguage(teacher.preferences.language);
        }

        // Ensure PDF options are set
        if (
          teacher &&
          (!teacher.preferences?.pdfOptions ||
            Object.keys(teacher.preferences.pdfOptions).length === 0)
        ) {
          teacher.preferences = {
            ...teacher.preferences,
            pdfOptions: defaultPdfOptions,
          };
        }

        return {
          token,
          teacher,
          organizationId: userData.organizationId,
          avatar: userData.avatar,
        };
      } catch (e) {
        if ((e as AxiosError).response?.status === 401) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          throw Error((e as any).response.data.message);
        }
        return rejectWithValue("Login failed. Please try again.");
      }
    } else {
      return rejectWithValue("Login failed. Please try again.");
    }
  },
);

export const setLanguage = createAsyncThunk(
  "user/setLanguage",
  async (language: string, { getState, rejectWithValue }) => {
    try {
      const oldPreferences = (getState() as RootState).user.teacher
        ?.preferences;
      const response = await editPreferences({
        preferences: { ...oldPreferences, language },
      });
      window.location.reload();
      // If teacher doesn't have pdf options, set them to default
      if (
        response &&
        (!response.preferences?.pdfOptions ||
          Object.keys(response.preferences.pdfOptions).length === 0)
      ) {
        response.preferences = {
          ...response.preferences,
          pdfOptions: defaultPdfOptions,
        };
      }

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const setPremium = createAsyncThunk(
  "user/setPremium",
  async (proDate: Date, { getState, rejectWithValue }) => {
    try {
      const oldPreferences = (getState() as RootState).user.teacher
        ?.preferences;
      const response = await editPreferences({
        preferences: {
          ...oldPreferences,
          /* @ts-ignore */
          teacherProDate: proDate,
        },
      });
      // If teacher doesn't have pdf options, set them to default
      if (
        response &&
        (!response.preferences?.pdfOptions ||
          Object.keys(response.preferences.pdfOptions).length === 0)
      ) {
        response.preferences = {
          ...response.preferences,
          pdfOptions: defaultPdfOptions,
        };
      }

      window.location.reload();
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const setAssistantModel = createAsyncThunk(
  "user/setAssistantModel",
  async (modelId: string, { getState, rejectWithValue }) => {
    try {
      const oldPreferences = (getState() as RootState).user.teacher
        ?.preferences;
      const response = await editPreferences({
        preferences: {
          ...oldPreferences,
          preferredLanguageModel: modelId,
        },
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const toggleFavoriteTool = createAsyncThunk(
  "user/toggleFavoriteTool",
  async (toolId: string, { getState, rejectWithValue }) => {
    try {
      const oldPreferences = (getState() as RootState).user.teacher
        ?.preferences;
      const isFavorite = oldPreferences?.favoriteTools?.includes(toolId);

      let updatedFavoriteTools;
      if (isFavorite) {
        // Remove tool from favorites if already present
        updatedFavoriteTools = oldPreferences?.favoriteTools?.filter(
          (tool) => tool !== toolId,
        );
      } else {
        // Add tool to favorites if not present
        updatedFavoriteTools = [
          ...(oldPreferences?.favoriteTools ?? []),
          toolId,
        ];
      }

      const response = await editPreferences({
        preferences: {
          ...oldPreferences,
          favoriteTools: updatedFavoriteTools,
        },
      });
      // If teacher doesn't have pdf options, set them to default
      if (
        response &&
        (!response.preferences?.pdfOptions ||
          Object.keys(response.preferences.pdfOptions).length === 0)
      ) {
        response.preferences = {
          ...response.preferences,
          pdfOptions: defaultPdfOptions,
        };
      }

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateLastUsedTools = createAsyncThunk(
  "user/updateLastUsedTools",
  async (toolType: string, { getState, rejectWithValue }) => {
    try {
      const oldPreferences = (getState() as RootState).user.teacher
        ?.preferences;

      // Add the tool to the lastUsedTools array if it doesn't already exist
      const lastUsedTools = oldPreferences?.lastUsedTools || [];
      const updatedLastUsedTools = [...lastUsedTools];

      // Only add the tool if it's not already in the array
      if (!updatedLastUsedTools.includes(toolType)) {
        updatedLastUsedTools.push(toolType);
      }

      const response = await editPreferences({
        preferences: {
          ...oldPreferences,
          lastUsedTools: updatedLastUsedTools,
        },
      });
      // If teacher doesn't have pdf options, set them to default
      if (
        response &&
        (!response.preferences?.pdfOptions ||
          Object.keys(response.preferences.pdfOptions).length === 0)
      ) {
        response.preferences = {
          ...response.preferences,
          pdfOptions: defaultPdfOptions,
        };
      }

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updatePdfOptions = createAsyncThunk(
  "user/updatePdfOptions",
  async (
    pdfOptions: PdfCustomizationSettings,
    { getState, rejectWithValue },
  ) => {
    try {
      const oldPreferences =
        (getState() as RootState).user.teacher?.preferences || {};
      const teacher = (getState() as RootState).user.teacher;
      if (!teacher) return rejectWithValue("Teacher not found");

      const newPreferences = {
        ...oldPreferences,
        pdfOptions,
      };

      const response = await editPreferences({
        preferences: newPreferences,
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getCurrentTeacher = createAsyncThunk(
  "user/getCurrentTeacher",
  async (_, { getState }) => {
    const response = await getLoggedInTeacher();
    if (response?.preferences?.language) {
      i18n.changeLanguage(response.preferences.language);
    }

    // If teacher doesn't have pdf options, set them to default
    if (
      response &&
      (!response.preferences?.pdfOptions ||
        Object.keys(response.preferences.pdfOptions).length === 0)
    ) {
      response.preferences = {
        ...response.preferences,
        pdfOptions: defaultPdfOptions,
      };
    }

    const auth = getAuth();
    const user = auth.currentUser;
    const oldAvatar = (getState() as RootState).user.avatar;
    if (response) {
      console.log("identifying user");
      posthog.identify(response.id.toString(), {
        user_id: response.id.toString(),
        email: response.email,
        name: response.name,
      });
      Sentry.setUser({
        id: response.id.toString(),
        email: response.email,
        username: `${response.name} ${response.surname}`.trim(),
      });
      ReactGA.set({
        internal_user_id: response.id.toString(),
        user_email: response.email,
        user_name: `${response.name} ${response.surname}`.trim(),
        user_grade: "-1",
        user_premium: "false",
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    return [response, user?.photoURL ?? oldAvatar];
  },
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    addClassroom: (state, action: PayloadAction<ClassroomResponseDto>) => {
      const newClasses = [action.payload, ...(state.teacher?.classes ?? [])];
      if (state.teacher) {
        state.teacher.classes = newClasses;
      }

      localStorage.setItem("userState", JSON.stringify(state));
    },
    setUser: (
      state,
      action: PayloadAction<
        Omit<UserState, "isAuthenticated" | "loading" | "error">
      >,
    ) => {
      state.token = action.payload.token;
      state.teacher = action.payload.teacher;
      state.organizationId = action.payload.organizationId;
      state.isAuthenticated = true;
      // Save to localStorage
      localStorage.setItem("userState", JSON.stringify(state));
    },
    logout: (state) => {
      state.token = null;
      state.teacher = null;
      state.organizationId = null;
      state.isAuthenticated = false;
      state.error = null;
      const modal = localStorage.getItem("madlen_daily_info_modal_shown");
      localStorage.clear();
      if (modal) {
        localStorage.setItem("madlen_daily_info_modal_shown", modal);
      }
      window.location.href = "/";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload.token;
        state.teacher = action.payload.teacher;
        state.organizationId = action.payload.organizationId;
        state.avatar = action.payload.avatar;
        state.isAuthenticated = true;
        // Save to localStorage
        localStorage.setItem("userState", JSON.stringify(state));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(setLanguage.fulfilled, (state, action) => {
        state.teacher = action.payload;
      })
      .addCase(getCurrentTeacher.fulfilled, (state, action) => {
        const teacher = action.payload[0] as TeacherResponseDto;
        const avatar = action.payload[1] as string | null;
        state.teacher = teacher;
        state.avatar = avatar;

        if (teacher?.preferences?.language) {
          console.log("changing language to", teacher.preferences.language);
          i18next.changeLanguage(teacher.preferences.language);
        }
        localStorage.setItem("userState", JSON.stringify(state));
      })
      .addCase(toggleFavoriteTool.fulfilled, (state, action) => {
        state.teacher = action.payload;
        localStorage.setItem("userState", JSON.stringify(state));
      })
      .addCase(updateLastUsedTools.fulfilled, (state, action) => {
        state.teacher = action.payload;
        localStorage.setItem("userState", JSON.stringify(state));
      })
      .addCase(updatePdfOptions.fulfilled, (state, action) => {
        state.teacher = action.payload;
        localStorage.setItem("userState", JSON.stringify(state));
      })
      .addCase(setAssistantModel.fulfilled, (state, action) => {
        state.teacher = action.payload;
        localStorage.setItem("userState", JSON.stringify(state));
      })
      .addCase(setAssistantModel.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { setUser, logout, addClassroom } = userSlice.actions;
export default userSlice.reducer;
